import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';

const SendWhatsAppMessagePage = ({ customGroups = null, isAdmin = false }) => {
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState('');
  const [templateVars, setTemplateVars] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [showSendButton, setShowSendButton] = useState(false);
  const [response, setResponse] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendType, setSendType] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Template picker state
  const [templates, setTemplates] = useState([]);
  const [eventConfig, setEventConfig] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadingConfig, setLoadingConfig] = useState(true);

  const recipientOptions = ['All', ...(customGroups || ['Bride', 'Groom', 'Bridal Party', 'Event Staff'])];

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];

  useEffect(() => {
    fetchSendConfig();
  }, []);

  const fetchSendConfig = async () => {
    try {
      setLoadingConfig(true);
      const resp = await fetch(`${CONFIG.apiBaseUrl}/send-config`, {
        credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        setEventConfig(data.event || null);
        // Filter templates to WhatsApp or both
        const waTemplates = (data.templates || []).filter(
          t => t.channel === 'whatsapp' || t.channel === 'both'
        );
        setTemplates(waTemplates);
      }
    } catch (err) {
      console.error('Error fetching send config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  const getSelectedTemplate = () => {
    if (!selectedTemplateId) return null;
    return templates.find(t => t.template_id === selectedTemplateId) || null;
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    setShowPreview(false);
    setShowSendButton(false);
    setResponse(null);
    setMessage('');

    if (templateId === 'freeform') {
      setTemplateVars({});
      return;
    }

    const template = templates.find(t => t.template_id === templateId);
    if (!template) return;

    // Build variable values, pre-populating from event variables
    const vars = {};
    const eventVars = eventConfig?.variables || {};
    (template.variables || []).forEach(varName => {
      vars[varName] = eventVars[varName] || '';
    });
    setTemplateVars(vars);
  };

  const handleTemplateVarChange = (key, value) => {
    setTemplateVars(prev => ({ ...prev, [key]: value }));
  };

  const generateMessage = () => {
    const template = getSelectedTemplate();
    if (!template) return '';

    let msg = template.message_body;
    Object.entries(templateVars).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      msg = msg.replace(regex, value || `[${key}]`);
    });
    return msg;
  };

  const handleRecipientChange = (value) => {
    setRecipients(prev =>
      prev.includes(value)
        ? prev.filter(r => r !== value)
        : [...prev, value]
    );
  };

  const handlePreview = () => {
    setShowPreview(true);
    setShowSendButton(true);
    setResponse(null);
  };

  const handleSend = async () => {
    try {
      setSending(true);
      setShowSendButton(false);

      const endpoint = sendType === 'now'
        ? `${CONFIG.apiBaseUrl}/send_whatsapp_message`
        : `${CONFIG.apiBaseUrl}/schedule_whatsapp_message`;

      let scheduledTimeISO = null;
      if (sendType === 'scheduled' && scheduledDate && scheduledTime) {
        // Parse the user's input as wall-clock time in the selected timezone.
        // new Date(`${date}T${time}`) interprets the string as browser-local time,
        // which may differ from the selected timezone. Correct by computing the
        // delta between what the selected timezone reads at that browser-local
        // instant and adjusting accordingly.
        const naive = new Date(`${scheduledDate}T${scheduledTime}`);
        const inTZ = new Date(naive.toLocaleString('en-US', { timeZone: timezone }));
        scheduledTimeISO = new Date(naive.getTime() + (naive.getTime() - inTZ.getTime())).toISOString();
      }

      const isFreeform = selectedTemplateId === 'freeform';
      const selectedTmpl = getSelectedTemplate();

      const payload = isFreeform
        ? {
            from: 'webTest',
            recipients,
            message_body: message,
            ...(sendType === 'scheduled' && { scheduled_time: scheduledTimeISO, timezone }),
          }
        : {
            from: 'webTest',
            recipients,
            templateVars,
            whatsapp_template_name: selectedTmpl?.whatsapp_template_name || undefined,
            ...(sendType === 'scheduled' && {
              scheduled_time: scheduledTimeISO,
              timezone,
              message_body: generateMessage(),
            }),
          };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      const data = await resp.json();
      setResponse({ success: resp.ok, data });
    } catch (error) {
      console.error('Send WhatsApp message error:', error);
      setResponse({ success: false, error: error.message });
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setRecipients([]);
    setMessage('');
    setTemplateVars({});
    setSelectedTemplateId('');
    setShowPreview(false);
    setShowSendButton(false);
    setResponse(null);
    setSendType('now');
    setScheduledDate('');
    setScheduledTime('');
    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  };

  const toLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMinDate = () => {
    return toLocalDateString(new Date());
  };

  const getMaxDate = () => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    return toLocalDateString(oneYearFromNow);
  };

  const selectedTemplate = getSelectedTemplate();
  const isFreeform = selectedTemplateId === 'freeform';
  const hasSelection = selectedTemplateId !== '';
  const allFieldsFilled = isFreeform
    ? message.trim() !== '' && recipients.length > 0
    : selectedTemplate
      ? Object.values(templateVars).every(val => val.trim() !== '') && !!selectedTemplate.whatsapp_template_name && recipients.length > 0
      : false;

  return (
    <div className="message-page">
      <h2 className="page-title">
        Send WhatsApp Message
      </h2>

      <div className="message-form">
        {/* Template Picker */}
        <div className="template-picker-group">
          <label className="form-label">Pick a template:</label>
          {loadingConfig ? (
            <div className="loading-message">Loading templates...</div>
          ) : templates.length === 0 && !isAdmin ? (
            <div className="no-templates-message">
              No WhatsApp templates are assigned to your event. Please ask an admin to assign templates.
            </div>
          ) : (
            <select
              className="template-picker-select"
              value={selectedTemplateId}
              onChange={(e) => handleTemplateSelect(e.target.value)}
            >
              <option value="" disabled>-- Select a template --</option>
              {[...templates].sort((a, b) => a.template_name.localeCompare(b.template_name)).map(t => (
                <option key={t.template_id} value={t.template_id}>
                  {t.template_name}
                </option>
              ))}
              {isAdmin && <option value="freeform">Free-form message</option>}
            </select>
          )}
        </div>

        {/* Form shown after template selection */}
        {hasSelection && (
          <>
            <div className="form-group">
              <label className="form-label">Send To:</label>
              <div className="checkbox-group">
                {recipientOptions.map(option => (
                  <label key={option} className="checkbox-label">
                    <input
                      type="checkbox"
                      value={option}
                      checked={recipients.includes(option)}
                      onChange={(e) => handleRecipientChange(e.target.value)}
                      className="checkbox-input"
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Free-form mode: editable textarea */}
            {isFreeform && (
              <div className="form-group">
                <label htmlFor="waFreeformMessage" className="form-label">Message:</label>
                <textarea
                  id="waFreeformMessage"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows="5"
                  placeholder="Type your WhatsApp message here..."
                  className="message-textarea"
                />
                <div className="char-count">Characters: {message.length}</div>
                <div className="help-text">
                  Note: Free-form messages can only be sent within a 24-hour window after the guest last messaged you.
                </div>
              </div>
            )}

            {/* Template mode: show template body + variable inputs */}
            {selectedTemplate && (
              <>
                <div className="form-group">
                  <label className="form-label">Message Template:</label>
                  <div className="template-message-display">
                    {selectedTemplate.message_body}
                  </div>
                  {!selectedTemplate.whatsapp_template_name && (
                    <div className="response-box error" style={{ marginTop: '8px', padding: '10px 14px' }}>
                      <strong>Warning:</strong> This template is missing its WhatsApp Template Name. Please ask an admin to edit the template and add the exact name registered in your WhatsApp Business account before sending.
                    </div>
                  )}
                </div>

                {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Template Variables:</label>
                    <div className="template-var-inputs">
                      {selectedTemplate.variables.map(varName => (
                        <div key={varName} className="template-var-field">
                          <label>
                            {`{{${varName}}}`} <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            value={templateVars[varName] || ''}
                            onChange={(e) => handleTemplateVarChange(varName, e.target.value)}
                            placeholder={`Enter ${varName.replace(/_/g, ' ')}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label className="form-label">Delivery:</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    value="now"
                    checked={sendType === 'now'}
                    onChange={(e) => setSendType(e.target.value)}
                    className="radio-input"
                  />
                  <span>Send Now</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    value="scheduled"
                    checked={sendType === 'scheduled'}
                    onChange={(e) => setSendType(e.target.value)}
                    className="radio-input"
                  />
                  <span>Schedule for Later</span>
                </label>
              </div>
            </div>

            {sendType === 'scheduled' && (
              <>
                <div className="form-group">
                  <label htmlFor="scheduledDate" className="form-label">
                    Date:
                  </label>
                  <input
                    type="date"
                    id="scheduledDate"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={getMinDate()}
                    max={getMaxDate()}
                    className="datetime-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="scheduledTime" className="form-label">
                    Time:
                  </label>
                  <input
                    type="time"
                    id="scheduledTime"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="datetime-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="timezone" className="form-label">
                    Timezone:
                  </label>
                  <select
                    id="timezone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="timezone-select"
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                  <div className="help-text">
                    Auto-detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                  </div>
                </div>
              </>
            )}

            <button
              onClick={handlePreview}
              className="btn btn-preview"
              disabled={!allFieldsFilled}
            >
              Message Preview
            </button>

            {showPreview && (
              <div className="preview-box">
                <strong>
                  {sendType === 'now' ? 'You are about to send' : 'You are about to schedule'} a WhatsApp message to:
                </strong>
                <div className="preview-recipients">
                  {recipients.join(', ') || 'No one'}
                </div>
                {sendType === 'scheduled' && scheduledDate && scheduledTime && (
                  <>
                    <strong style={{ display: 'block', marginTop: '12px' }}>
                      Scheduled for:
                    </strong>
                    <div className="preview-schedule">
                      {new Date(`${scheduledDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}{', '}
                      {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      {' '}({timezone.replace(/_/g, ' ')})
                    </div>
                  </>
                )}
                <strong style={{ display: 'block', marginTop: '12px' }}>
                  With the following message:
                </strong>
                <div className="preview-message">{isFreeform ? message : generateMessage()}</div>
                <div className="preview-note">
                  This message will be sent via WhatsApp{isFreeform ? ' (free-form)' : ''}
                </div>
              </div>
            )}

            {showSendButton && (
              <button
                onClick={handleSend}
                disabled={sending}
                className="btn btn-send"
              >
                {sending
                  ? (sendType === 'scheduled' ? 'Scheduling WhatsApp...' : 'Sending WhatsApp...')
                  : (sendType === 'scheduled' ? 'Schedule WhatsApp Message' : 'Send WhatsApp Message')
                }
              </button>
            )}

            {response && (
              <div className={`response-box ${response.success ? 'success' : 'error'}`}>
                {response.success ? (
                  <>
                    <strong>
                      {sendType === 'scheduled' ? 'WhatsApp Message Scheduled Successfully!' : 'WhatsApp Messages Sent Successfully!'}
                    </strong>
                    {sendType === 'scheduled' ? (
                      <div style={{ marginTop: '10px' }}>
                        <p>Your WhatsApp message has been scheduled and will be sent at the specified time.</p>
                        {response.data?.scheduled_message?.scheduled_time && (
                          <p>Scheduled for: {new Date(response.data.scheduled_message.scheduled_time).toLocaleString('en-US', {
                            timeZone: timezone,
                            dateStyle: 'full',
                            timeStyle: 'short',
                          })}</p>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginTop: '10px' }}>
                        {response.data?.successful > 0 && (
                          <p>Successfully sent to {response.data.successful} contact(s)</p>
                        )}
                        {response.data?.failed > 0 && (
                          <p style={{ color: '#d97706' }}>Failed to send to {response.data.failed} contact(s)</p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <strong>Error Occurred</strong>
                    <pre style={{ marginTop: '10px' }}>
                      {JSON.stringify(response.data || response.error, null, 2)}
                    </pre>
                  </>
                )}
              </div>
            )}

            <button onClick={handleReset} className="btn btn-reset">
              Reset Page
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SendWhatsAppMessagePage;
